import moment from 'moment';
import React, { useMemo } from 'react';
import TimelineChart, {
  TimelineChartDataPoint,
} from '../../../../components/voxel/Charts/TimelineChart';
import Legend from '../Legend';
import { ForwardedChartProps } from './ProgressDashboardSite';
import { DataPoint } from './models';

export interface ProgressDashboardSiteChartKeywordDistributionProps {
  progressAnalysisData: DataPoint[];
  timelineChartProps: ForwardedChartProps;
  legend?: boolean;
}

const dataColors = [
  {
    fillClass: 'fill-performanceScale-13',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-13/75',
    tooltipOuterCircleClass: 'fill-performanceScale-13/30',
  },
  {
    fillClass: 'fill-performanceScale-410',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-410/75',
    tooltipOuterCircleClass: 'fill-performanceScale-410/30',
  },
  {
    fillClass: 'fill-performanceScale-1120',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-1120/50',
    tooltipOuterCircleClass: 'fill-performanceScale-1120/25',
  },
  {
    fillClass: 'fill-performanceScale-2150',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-2150/50',
    tooltipOuterCircleClass: 'fill-performanceScale-2150/25',
  },
  {
    fillClass: 'fill-performanceScale-51100',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-51100/50',
    tooltipOuterCircleClass: 'fill-performanceScale-51100/25',
  },
  {
    fillClass: 'fill-performanceScale-101',
    strokeClass: 'stroke-white',
    tooltipInnerCircleClass: 'fill-performanceScale-101/50',
    tooltipOuterCircleClass: 'fill-performanceScale-101/25',
  },
];

export const ProgressDashboardSiteChartKeywordDistribution: React.FC<ProgressDashboardSiteChartKeywordDistributionProps> =
  ({ progressAnalysisData, timelineChartProps, legend }) => {
    const dataSeries: TimelineChartDataPoint[][] = useMemo(
      () => [
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_1_3_count,
          })
        ),
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_4_10_count,
          })
        ),
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_11_20_count,
          })
        ),
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_21_50_count,
          })
        ),
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_51_100_count,
          })
        ),
        progressAnalysisData?.map(
          (data): TimelineChartDataPoint => ({
            date: moment(data.scheduledTime).toDate(),
            value: data.rank_101_count,
          })
        ),
      ],
      [progressAnalysisData]
    );

    if (!progressAnalysisData?.length) return null;
    return (
      <div className="flex flex-row items-center">
        <TimelineChart
          dataColors={dataColors}
          dataSeries={dataSeries}
          yLabelTooltip="Keywords"
          stacked
          yMaxPlaceholder={1}
          {...timelineChartProps}
          showYGrid={false}
          width={timelineChartProps.width}
        />
        {legend && (
          <div className="w-[120px] pl-2">
            <Legend
              loadingPlaceholder={timelineChartProps.loadingPlaceholder}
              dataSeriesInfo={[
                {
                  name: '1-3',
                  fillColorClass: 'fill-performanceScale-13',
                  startValue: dataSeries[0][0].value,
                  latestValue: dataSeries[0][dataSeries[0].length - 1].value,
                },
                {
                  name: '4-10',
                  fillColorClass: 'fill-performanceScale-410',
                  startValue: dataSeries[1][0].value,
                  latestValue: dataSeries[1][dataSeries[0].length - 1].value,
                },
                {
                  name: '11-20',
                  fillColorClass: 'fill-performanceScale-1120',
                  startValue: dataSeries[2][0].value,
                  latestValue: dataSeries[2][dataSeries[0].length - 1].value,
                },
                {
                  name: '21-50',
                  fillColorClass: 'fill-performanceScale-2150',
                  startValue: dataSeries[3][0].value,
                  latestValue: dataSeries[3][dataSeries[0].length - 1].value,
                },
                {
                  name: '51-100',
                  fillColorClass: 'fill-performanceScale-51100',
                  startValue: dataSeries[4][0].value,
                  latestValue: dataSeries[4][dataSeries[0].length - 1].value,
                },
                {
                  name: '100+',
                  fillColorClass: 'fill-performanceScale-101',
                  startValue: dataSeries[5][0].value,
                  latestValue: dataSeries[5][dataSeries[0].length - 1].value,
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  };

export default ProgressDashboardSiteChartKeywordDistribution;
